import { useRouter } from 'next/router';
import type { FC } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useCategoryBrands } from 'src/hooks/useCategoryBrands';
import { I18nContext } from 'src/stores/I18n';
import { smoothScrollTo } from 'src/utils/scrollToElement';
import { FOOTER_IMAGE_LIST, HEADER_HEIGHT } from '../AllBrands';
import { Presenter } from './Presenter';

const INITIAL_VISIBLE_COUNT = 5;
const INITIAL_EXPANDED_INDEX = 0;

type Props = {
  headingLevel: 'h2' | 'h3';
};

export const CategoryBrands: FC<Props> = ({ headingLevel }) => {
  const { pathname } = useRouter();
  const initialEndRef = useRef<HTMLDivElement | null>(null);
  const listEndRef = useRef<HTMLDivElement | null>(null);
  const categoryRefs = useRef<(HTMLLIElement | null)[]>([]);
  const { parentCategory, subCategories, hasSubCategories } = useCategoryBrands();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(INITIAL_EXPANDED_INDEX);
  const [visibleCount, setVisibleCount] = useState<number>(INITIAL_VISIBLE_COUNT);
  const [isSeeMoreButtonExpanded, setIsSeeMoreButtonExpanded] = useState(false);
  const localeResources = useContext(I18nContext);

  useEffect(() => {
    setExpandedIndex(INITIAL_EXPANDED_INDEX);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    setIsSeeMoreButtonExpanded(false);
  }, [pathname]);

  useEffect(() => {
    if (expandedIndex) {
      const categoryElement = categoryRefs.current[expandedIndex];
      categoryElement?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [expandedIndex]);

  const handleClickSubCategory = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleToggleClick = () => {
    setIsSeeMoreButtonExpanded(!isSeeMoreButtonExpanded);
    if (isSeeMoreButtonExpanded) {
      setVisibleCount(INITIAL_VISIBLE_COUNT);
      const targetY = initialEndRef.current?.getBoundingClientRect().top! + window.scrollY - HEADER_HEIGHT;
      smoothScrollTo(targetY, 500);
    } else {
      setVisibleCount(subCategories.length);
      listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };
  if (!parentCategory || !subCategories) return null;

  return (
    <Presenter
      handleToggleClick={handleToggleClick}
      handleClickSubCategory={handleClickSubCategory}
      initialVisibleCount={INITIAL_VISIBLE_COUNT}
      initialEndRef={initialEndRef}
      listEndRef={listEndRef}
      categoryRefs={categoryRefs}
      Heading={headingLevel}
      headingTitle={`${parentCategory?.name}${localeResources.brands.list.title}`}
      expandedIndex={expandedIndex}
      isSeeMoreButtonExpanded={isSeeMoreButtonExpanded}
      visibleCount={visibleCount}
      footerImageList={FOOTER_IMAGE_LIST}
      data={subCategories}
      hasSubCategories={hasSubCategories}
    />
  );
};
